import { Entity, formatTicker } from '@api';
import { RangeHorizon } from '@chart';
import { ButtonGroup } from '@design';
import { useElementSize } from '@helpers';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { TeamsGuard } from '@terminal/config/TeamsGuard';
import { useCompanyCompetitors } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '@terminal/routes/app-paths';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import {
  getChartSubPageSearchParams,
  SnakeTypes,
} from '@terminal/widgets/chart-peer-group/utils/chart-subpage-navigation/chart-subpage-navigation';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ValuationMetrics } from '../valuation-metrics/ValuationMetrics';
import styles from './OverviewValuationMetric.module.scss';
import { useValuationSnakeData } from './useValuationSnakeData';
import {
  CHARTS_GAP,
  DISPLAYED_ENTITIES,
  getBarChartDimension,
  SNAKES_NTM,
  SNAKES_TTM,
} from './utils';

export interface OverviewValuationProps {
  entity: Entity;
}

export const OverviewValuation = ({ entity }: OverviewValuationProps) => {
  const { t } = useTranslation('widget');
  const { colors } = useChartTheme(['soft', 'multiline']);
  const [activeValuation, setActiveValuation] = useState(0);
  const [searchParams] = useSearchParams();

  const {
    competitorsData,
    isFetching,
    isError: isCompetitorsError,
    error: competitorsError,
  } = useCompanyCompetitors({
    entityTag: entity.tag,
  });

  const chartSubPageSearchParams = getChartSubPageSearchParams({
    entityTag: entity.tag,
    prevSearchParams: searchParams,
    horizon: RangeHorizon.OneYear,
    snakeType: SnakeTypes.ValuationMetrics,
  }).toString();

  const containerRef = useRef(null);
  const { width } = useElementSize(containerRef);
  const isDrawer = width < 600;

  const multiColors = colors.getMulti(competitorsData.length);
  const formattedCompetitorsData =
    competitorsData.slice(0, DISPLAYED_ENTITIES - 1).map((s, index) => ({
      tag: s.entity.tag,
      formattedTicker: formatTicker(s.entity),
      color: multiColors[index + 1].value,
    })) ?? [];

  const companies = [
    {
      tag: entity.tag,
      formattedTicker: formatTicker(entity),
      color: multiColors[0].value,
    },
    ...formattedCompetitorsData,
  ];

  const linkToChartSubPage = `${appPaths.assetOverview}?${chartSubPageSearchParams}`;

  const variants = {
    ttm: SNAKES_TTM,
    ntm: SNAKES_NTM,
  };
  const { data, isLoading } = useValuationSnakeData({
    snakes: variants,
    companies,
    enabled: !isFetching,
  });

  const hasDataTTM = data.ttm.length;
  const hasDataNTM = data.ntm.length;

  if ((!hasDataTTM && !hasDataNTM) || isLoading) {
    return null;
  }

  if (isCompetitorsError) {
    if (competitorsError?.status === 404) {
      return null;
    }

    return <ErrorMessageWrapper displayIcon />;
  }

  const labels = [
    { label: t('widget:assetOverview.ttm'), enabled: hasDataTTM },
    { label: t('widget:assetOverview.ntm'), enabled: hasDataNTM },
  ]
    .filter(l => l.enabled)
    .map(l => l.label);

  const variantData = Object.values(data)[activeValuation] as {
    data: {
      value: number;
      index: string;
    }[];
    snake: string;
  }[];

  const groupedData = Object.groupBy(variantData, s => s.snake.split('.')[1]);
  return (
    <div>
      <div className={styles.header}>
        <div className={styles.sectionTitleWrapper}>
          <TeamsGuard>
            <SectionTitle
              title={t('widget:assetOverview.valuationMetrics')}
              linkTo={linkToChartSubPage}
            />
          </TeamsGuard>
        </div>
        <ButtonGroup
          className={styles.styledButtonGroup}
          data-testid={'valuation-metrics-ttm-htm-button-group'}
          labels={labels}
          onClick={setActiveValuation}
        />
      </div>
      <div
        className={styles.flex}
        ref={containerRef}
        style={{ gap: `${CHARTS_GAP}px` }}
      >
        {Object.entries(groupedData).map(
          ([snakeName, snakesData], index, arr) => (
            <ValuationMetrics
              key={index}
              snakesData={snakesData ?? []}
              snakeName={snakeName}
              companies={companies}
              {...getBarChartDimension({
                width,
                isDrawer,
                chartsLength: arr.length,
              })}
            />
          )
        )}
      </div>
    </div>
  );
};
